import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { RotateCcw, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bouton + dialog « Faire repasser » un QCM / une évaluation : nouvelle tentative pour tous, les candidats en échec, ou une sélection
export const RepasserButton = ({ dossierId, testType, testId, titre, dateJournee, onDone, size = 'sm', className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" size={size} variant="outline" className={`h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50 ${className}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`repasser-btn-${testId}`}>
        <RotateCcw className="w-3 h-3 mr-1" /> Faire repasser
      </Button>
      {open && <RepasserDialog dossierId={dossierId} testType={testType} testId={testId} titre={titre} dateJournee={dateJournee} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
};

export const RepasserDialog = ({ dossierId, testType, testId, titre, dateJournee, onClose, onDone }) => {
  const [candidats, setCandidats] = useState(null);
  const [mode, setMode] = useState('echec');
  const [sel, setSel] = useState([]);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const url = testType === 'qcm' ? `${API_URL}/qcms/${testId}/resultats-dossier/${dossierId}` : `${API_URL}/evaluations-manuelles/${testId}/resultats-dossier/${dossierId}`;
    Promise.all([axios.get(url), axios.get(`${API_URL}/dossiers/${dossierId}/repassages`, { params: { test_type: testType, test_id: testId } })])
      .then(([r, rep]) => {
        const ouverts = new Set(rep.data.filter(x => x.statut === 'ouvert').map(x => x.candidat_id));
        const passes = (r.data.passages || []).map(p => ({ id: p.candidat_id, nom: p.candidat_nom || p.nom || '', score: testType === 'qcm' ? p.pourcentage : p.moyenne, echec: testType === 'qcm' ? (!p.reussi || (p.competences_resultats || []).some(c => c.valeur === 'NA')) : ((p.statut_global && p.statut_global !== 'A') || (p.resultats || []).some(c => c.valeur !== 'A')), nb: p.nb_tentatives || 1, ouvert: ouverts.has(p.candidat_id) }));
        const nonPasses = [...(r.data.non_passes || []), ...(r.data.non_evalues || [])].map(p => ({ id: p.candidat_id, nom: p.candidat_nom || `${p.nom || ''} ${p.prenom || ''}`, score: null, echec: false, nb: 0, ouvert: ouverts.has(p.candidat_id), jamais: !p.repassage }));
        const all = [...passes, ...nonPasses].sort((a, b) => a.nom.localeCompare(b.nom));
        setCandidats(all);
        setSel(all.filter(c => c.echec && !c.ouvert).map(c => c.id));
      }).catch(() => setCandidats([]));
  }, [dossierId, testType, testId]);
  const pick = (m) => {
    setMode(m);
    if (!candidats) return;
    if (m === 'tous') setSel(candidats.filter(c => !c.ouvert && !c.jamais).map(c => c.id));
    if (m === 'echec') setSel(candidats.filter(c => c.echec && !c.ouvert).map(c => c.id));
  };
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/dossiers/${dossierId}/repassages`, { test_type: testType, test_id: testId, candidat_ids: sel, date_journee: dateJournee || null, motif });
      toast.success(`Nouvelle tentative autorisée pour ${r.data.nb} candidat(s)`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setBusy(false); }
  };
  const fmtScore = (v) => (v == null ? '—' : `${Math.round(v)} %`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="repasser-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-amber-600" /> Faire repasser — {titre}</DialogTitle></DialogHeader>
        {!candidats ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">Les candidats sélectionnés pourront passer une nouvelle tentative (QCM disponible à nouveau sur leur portail / évaluation à re-noter). La <strong>meilleure tentative</strong> est retenue dans les bilans ; la chronologie est conservée.</p>
            <div className="flex gap-1.5 flex-wrap">
              {[['echec', 'En échec / non acquis'], ['tous', 'Tous les candidats'], ['selection', 'Sélection manuelle']].map(([m, l]) => (
                <button key={m} type="button" onClick={() => pick(m)} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${mode === m ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-testid={`repasser-mode-${m}`}>{l}</button>
              ))}
            </div>
            <ul className="max-h-60 overflow-y-auto divide-y rounded-lg border" data-testid="repasser-liste">
              {candidats.map(c => (
                <li key={c.id} className={`px-3 py-1.5 flex items-center gap-2 text-xs ${c.ouvert || c.jamais ? 'opacity-50' : ''}`} data-testid={`repasser-cand-${c.id}`}>
                  <Checkbox checked={sel.includes(c.id)} disabled={c.ouvert || c.jamais} onCheckedChange={(v) => { setMode('selection'); setSel(s => v ? [...s, c.id] : s.filter(x => x !== c.id)); }} data-testid={`repasser-check-${c.id}`} />
                  <span className="flex-1 font-medium text-slate-800 truncate">{c.nom}</span>
                  {c.jamais ? <span className="text-slate-400">pas encore passé</span> : c.ouvert ? <span className="text-amber-700">nouvelle tentative déjà ouverte</span> : (
                    <span className={c.echec ? 'text-red-600 font-semibold' : 'text-emerald-700'}>{fmtScore(c.score)}{c.nb > 1 ? ` · ${c.nb} tent.` : ''}{c.echec ? ' · à revoir' : ''}</span>
                  )}
                </li>
              ))}
              {candidats.length === 0 && <li className="px-3 py-3 text-xs text-slate-400 italic">Aucun candidat.</li>}
            </ul>
            <Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Motif (optionnel) — ex. module reconduit le 19/03" className="h-8 text-xs" data-testid="repasser-motif" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button className="bg-amber-600 hover:bg-amber-700" disabled={busy || sel.length === 0} onClick={go} data-testid="repasser-confirmer">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Autoriser ${sel.length} nouvelle(s) tentative(s)`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
